/**
 * Astor Portfolio Insights DTO — borrower portal's portfolio-wide cards strip.
 *
 * Renders as a row of up to four context cards at the top of the
 * 21-consumer-portal dashboard:
 *
 *   ┌──────────────────────┐  ┌──────────────────────┐
 *   │ AGING                │  │ MIN DSCR             │
 *   │ 3 docs aging past    │  │ DSCR coverage has    │
 *   │ 7 days               │  │ tightened            │
 *   │ Upload these to keep │  │ Case C-001 is now at │
 *   │ your loans moving.   │  │ 1.15x ...            │
 *   │  [3]    AGING        │  │  [1.15x] MIN DSCR    │
 *   └──────────────────────┘  └──────────────────────┘
 *
 * Wave 2 — DTO contract + deterministic projection in 8-agent-service. Future
 * waves may swap the heuristic ladder for an LLM-driven Astor twin without
 * changing this DTO surface.
 *
 * The cards are borrower-scoped (portfolio-wide, not case-scoped) and ordered
 * by priority (amber before mint). Empty array = nothing notable; the UI
 * renders an empty state in that case.
 */

import { z } from 'zod';

// ============================================================================
// Card tone enum
// ============================================================================

/**
 * Visual tone for the card. Drives the surface color + accent:
 *
 *   - `AMBER`   — warning tone (stale docs, tight DSCR)
 *   - `MINT`    — positive tone (below-market rate, on-track milestones)
 *   - `NEUTRAL` — informational tone (reserved for future card types)
 */
export const AstorInsightToneSchema = z.enum(['AMBER', 'MINT', 'NEUTRAL']);
export type AstorInsightTone = z.infer<typeof AstorInsightToneSchema>;

// ============================================================================
// Card
// ============================================================================

/**
 * A single insight card surfaced on the borrower dashboard. Each card carries
 * a headline + body and may include a metric pill ("3", "1.15x", "6.2%") plus
 * a deep-link href the UI uses for the card's primary action.
 */
export const AstorInsightCardSchema = z.object({
  /** Stable identifier — UUID per render. Lets the UI key list entries without thrashing. */
  id: z.string().uuid(),
  tone: AstorInsightToneSchema,
  /**
   * Short single-line headline. Max 120 chars to fit on a single card row at
   * mobile width. Examples:
   *   - "3 docs aging past 7 days"
   *   - "DSCR coverage has tightened"
   *   - "Your portfolio averages below market"
   */
  headline: z.string().min(1).max(120),
  /**
   * Two- to three-line supporting body that explains the headline. Max 280
   * chars to keep cards visually balanced.
   */
  body: z.string().min(1).max(280),
  /**
   * Optional metric pill (the big-number on the card). Example: "3", "1.15x",
   * "6.2%". Capped short so it never exceeds `body1` typography (the
   * page-title-as-ceiling invariant from portals.md §11b2).
   */
  metric: z.string().min(1).max(16).optional(),
  /**
   * Small uppercase label next to the metric (e.g. "AGING", "MIN DSCR",
   * "AVG RATE"). Rendered with `overline` typography in the UI.
   */
  metricLabel: z.string().min(1).max(32).optional(),
  /**
   * Optional click-through deep link. When present, the entire card becomes
   * a clickable surface that routes the borrower to the relevant detail page
   * (e.g. `/documents`, `/loans/{caseId}`).
   */
  href: z.string().min(1).max(512).optional(),
});
export type AstorInsightCardDto = z.infer<typeof AstorInsightCardSchema>;

// ============================================================================
// Root — portfolio insights envelope
// ============================================================================

/**
 * The portfolio-insights envelope returned by the agent service for a given
 * borrower. Sorted by priority — amber tones first so warnings render before
 * positive cards. Capped at 4 cards total to keep the strip visually
 * balanced.
 */
export const AstorPortfolioInsightsSchema = z.object({
  /** Up to 4 cards ordered by priority. Empty array = nothing notable. */
  cards: z.array(AstorInsightCardSchema).max(4).default([]),
  /** ISO timestamp of when the projection was generated. */
  generatedAt: z.string().datetime(),
});
export type AstorPortfolioInsightsDto = z.infer<typeof AstorPortfolioInsightsSchema>;
